import {
  Component,
  ViewChild,
  ViewContainerRef,
  Input,
  inject,
  effect,
  computed,
  signal,
  Output,
  EventEmitter,
} from '@angular/core';
import { ReaderDocument, WidgetType, WidgetKind } from '../models/reader-document';
import { WIDGET_REGISTRY } from '../registry/widget-registry.token';
import { UnsupportedWidgetComponent } from '../widgets/unsupported/unsupported.component';

@Component({
  selector: 'wr-reader-shell',
  standalone: true,
  template: `
    <div
      class="wr-reader-root pb-3 h-full"
      [style.min-height]="previewSig() ? null : '100vh'"
      [style.background]="background()"
      (click)="onRootClick($event)"
    >
      <ng-container #host></ng-container>
    </div>
  `,
})
export class ReaderShellComponent {
  @ViewChild('host', { read: ViewContainerRef, static: true })
  host!: ViewContainerRef;

  @Output() formPrepared = new EventEmitter<any>();

  private registry = inject(WIDGET_REGISTRY);
  private docSig = signal<ReaderDocument | null>(null);
  private systemDarkMode = signal<boolean>(false);
  private darkModeOverride = signal<boolean | null>(null);
  private isDarkMode = computed(() => this.darkModeOverride() ?? this.systemDarkMode());
  previewSig = signal<boolean>(false);
  private preferredLang = signal<string | null>(null);

  @Input() set document(value: ReaderDocument | null) {
    this.docSig.set(value);
  }

  /**
   * Preview mode (e.g. embedded in an editor): no form submission, no link navigation,
   * no page-level side effects (theme-color meta), no forced full-viewport height.
   */
  @Input() set preview(value: boolean | null | undefined) {
    this.previewSig.set(!!value);
  }

  /** Forces light/dark rendering. `null`/`undefined` follows the system color scheme. */
  @Input() set darkMode(value: boolean | null | undefined) {
    this.darkModeOverride.set(value ?? null);
  }

  onRootClick(event: MouseEvent) {
    if (!this.previewSig()) return;
    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (anchor) event.preventDefault();
  }

  background = () => {
    const theme = this.docSig()?.theme;
    const dark = this.isDarkMode();

    if (!theme) return '#ffffff';

    return dark
      ? theme.darkColors.background
      : theme.lightColors.background;
  };

  private applyFonts(theme: any) {
    if (!theme?.fonts) return;

    // preload each font
    for (const font of Object.values(theme.fonts) as any[]) {
      if (!font?.url || !font?.name) continue;

      // avoid duplicate preload tags
      if (!document.querySelector(`link[data-wr-font='${font.name}']`)) {
        const preload = document.createElement('link');
        preload.rel = 'preload';
        preload.as = 'font';
        preload.href = font.url;
        preload.type = 'font/ttf';
        preload.crossOrigin = 'anonymous';
        preload.setAttribute('data-wr-font', font.name);
        document.head.appendChild(preload);
      }
    }

    // add font face defs
    let css = '';

    for (const font of Object.values(theme.fonts) as any[]) {
      if (!font?.url || !font?.name) continue;

      css += `
        @font-face {
          font-family: '${font.name}';
          src: url('${font.url}') format('truetype');
          font-display: swap; /* avoids invisible text */
        }
      `;
    }

    // add stylesheet
    let styleEl = document.getElementById('wr-fonts') as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'wr-fonts';
      document.head.appendChild(styleEl);
    }

    styleEl.textContent = css;
  }

    private applyThemeColor(theme: any, isDark: boolean) {
    const color =
      (isDark ? theme?.darkColors?.background : theme?.lightColors?.background) ??
      '#ffffff';

    // Standard (Chrome/Android + Safari newer)
    let meta = document.querySelector(`meta[name="theme-color"]`) as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = color;

    // Optional: Safari iOS 15+ supports media-specific theme-color.
    // <meta name="theme-color" media="(prefers-color-scheme: dark)" content="...">
    // <meta name="theme-color" media="(prefers-color-scheme: light)" content="...">
  }


  private detectBrowserLanguage(): string | null {
    const nav = window.navigator as any;
    const raw = (nav.language || nav.userLanguage || '') as string;
    if (!raw) return null;

    // e.g. "de-DE" => "de"
    const short = raw.split('-')[0].toLowerCase();
    return short || null;
  }

  private resolveActiveLanguage(doc: ReaderDocument): string | null {
    const preferred = this.preferredLang();
    const available = doc.languages ?? [];
    const base = doc.language ?? null;

    if (preferred && available.includes(preferred)) {
      return preferred;
    }

    return base;
  }

  private resolveRenderType(node: WidgetType): WidgetKind {
    const t = node.type as WidgetKind;
    // treat these as images widget
    if (
      t === 'imagesHorizontal' ||
      t === 'imageSlider' ||
      t === 'imageBlocks'
    ) {
      return 'images';
    }

    return t;
  }

  // TODO: delete this?

  private localizeWidget<T extends WidgetType>(
    widget: T,
    translations?: Record<string, string>
  ): T {
    if (!translations) return widget;

    // avoid mutating original
    const clone = { ...widget } as any;
    const baseKey = widget.id;

    // DISABLED - NOT NECESSARY?
/*     if ('text' in widget && typeof widget.text === 'string') {
      const key = `${baseKey}.text`;
      if (translations[key]) {
        clone.text = translations[key];
      }
    } */

    return clone as T;
  }

  cornerRadius = () => this.docSig()?.theme?.cornerRadius ?? 0;

  constructor() {
    // detect initial system theme
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemDarkMode.set(mq.matches);

    // watch for mode change
    mq.addEventListener('change', (e) => {
      this.systemDarkMode.set(e.matches);
    });
    
    // detect browser language
    const detected = this.detectBrowserLanguage();
    this.preferredLang.set(detected);

    effect(() => {
      const doc = this.docSig();
      if (!doc || !this.host) return;

      const theme = doc.theme;
      const isDark = this.isDarkMode();

      this.applyFonts(theme);
      if (!this.previewSig()) this.applyThemeColor(theme, isDark);
      this.host.clear();

      const spacing = theme?.widgetSpacing ?? 0;

      const activeLang = this.resolveActiveLanguage(doc);
      const translationMap =
        activeLang && (doc as any).translations
          ? (doc as any).translations[activeLang]
          : undefined;

      for (let i = 0; i < doc.widgets.length; i++) {
        const node = doc.widgets[i];

        const renderType = this.resolveRenderType(node);
        const cmp =
          this.registry[renderType] ??
          UnsupportedWidgetComponent;

        const localizedNode = this.localizeWidget(node, translationMap);
        const ref = this.host.createComponent(cmp);

        if (!(renderType in this.registry)) {
          (ref.instance as any).type = renderType;
        } else {
          (ref.instance as any).json = localizedNode;
        }

        if (theme) {
          (ref.instance as any).theme = {
            ...theme,
            isDark,
          };
        }

        // TODO: Necessary? Refactor?

        if (node.type === 'form' && !this.previewSig()) {
          const inst: any = ref.instance;
          inst.prepared?.subscribe?.((payload: any) => {
            //console.log('Form payload from widget (via shell):', payload);
            this.formPrepared.emit(payload);
          });
        }


        const el: HTMLElement = (ref.location as any).nativeElement;

        // widget spacing
        if (i !== doc.widgets.length - 1) {

          if (node.type === 'image' && node.url === '') {
            el.style.display = 'none';
          }
          else if (node.type === 'images' && node.images.length === 0){
            el.style.display = 'none';
          }
          else{
            el.style.display = 'block';
            el.style.marginBottom = `${theme?.widgetSpacing ?? 0}px`;
          }

        }
        // widget id
        if ((node as any).id) {
          el.id = (node as any).id;
        }
      }
    });
  }
}
